import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { Col, Container, Row, Tab, Tabs } from "react-bootstrap";
import { useQuery } from "react-query";

import Spinner from "@components/Spinner/Spinner";
import ServerError from "@components/Error/ServerError";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import TreeDetail from "@modules/Trees/components/TreeDetail";
import TreeListMobileView from "@modules/Trees/components/TreeListMobileView";
import SensorDetail from "@modules/Sensors/components/SensorDetail";
import SensorMobileListView from "@modules/Sensors/components/SensorMobileListView";
import IrrigationAreaList from "@modules/IrrigationArea/components/IrrigationAreaList";
import IrrigationAreaDetail from "@modules/IrrigationArea/components/IrrigationAreaDetail";
import TreeList from "./TreeList";
import SensorList from "./SensorList";
import { getAllTrees } from "@modules/Trees/services/treeCrud";
import { ViewTypeButton } from "@modules/Projects/pages/ProjectDetail/Button";
import useMatchedElementHeight from "@hooks/useMatchedElementHeight";

export default function WithoutProjectView() {
  const { t } = useTranslation();
  const { listType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [trees, setTrees] = useState([]);
  const [areas, setAreas] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [formValue, setFormValue] = useState();
  const [, setIsTreeUpdate] = useState(false);
  const [, setOpenTreeForm] = useState(false);
  const [openImageForm, setOpenImageForm] = useState(false);
  const [aid, setAid] = useState();
  const { mobile } = useSelector((state) => state.mobile);
  const query = new URLSearchParams(location.search);
  const tid = query.get("selectedTree");
  const sid = query.get("selectedSensor");
  const id = query.get("selectedArea");
  const noop = () => undefined;
  const treeListColumnRef = React.useRef(null);
  const sensorListColumnRef = React.useRef(null);
  const treeDetailHeight = useMatchedElementHeight(
    treeListColumnRef,
    ".table-wrapper",
    !mobile && tid
  );
  const sensorDetailHeight = useMatchedElementHeight(
    sensorListColumnRef,
    ".table-wrapper",
    !mobile && sid
  );

  // Fetch all trees
  const {
    data: allTrees,
    isLoading,
    isError,
  } = useQuery("trees-without-project", getAllTrees, {
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Determine active tab from URL parameter
  const validTabs = ["areas", "trees", "sensors"];
  const activeTab = validTabs.includes(listType) ? listType : "trees";

  // Update URL when tab changes
  const handleTabSelect = (key) => {
    const query = new URLSearchParams(location.search);

    // Remove selection parameters that don't belong to the new tab
    if (key === "areas") {
      query.delete("selectedTree");
      query.delete("selectedSensor");
    } else if (key === "trees") {
      query.delete("selectedArea");
      query.delete("selectedSensor");
    } else if (key === "sensors") {
      query.delete("selectedArea");
      query.delete("selectedTree");
    }

    navigate(
      `/project/without-project/list/${key}${query.toString() ? `?${query.toString()}` : ""}`,
      {
        replace: true,
      }
    );
  };

  // Redirect to default tab if no valid tab is in URL
  useEffect(() => {
    if (!listType || !validTabs.includes(listType)) {
      const query = new URLSearchParams(location.search);
      navigate(
        `/project/without-project/list/trees${query.toString() ? `?${query.toString()}` : ""}`,
        {
          replace: true,
        }
      );
    }
  }, [listType, location.search, navigate]);

  useEffect(() => {
    if (!allTrees) return;

    // Filter trees that have no project_id or project_id is null
    const treesWithoutProject = allTrees
      .filter((tree) => !tree.project_id)
      .map((tree) => ({
        ...tree,
        area: tree.area_name || t("without_location"),
        areaId: tree.area_id || null,
      }));

    setTrees(treesWithoutProject);

    // Extract sensors from trees
    const sensorsFromTrees = treesWithoutProject.flatMap((tree) =>
      (tree.installations || []).map((installation) => ({
        ...installation.sensor,
        tree: tree.name,
      }))
    );
    setSensors(sensorsFromTrees);

    // Group areas (for trees that have an area)
    const areasMap = {};
    treesWithoutProject.forEach((tree) => {
      if (tree.area_id) {
        if (!areasMap[tree.area_id]) {
          areasMap[tree.area_id] = {
            id: tree.area_id,
            name: tree.area_name || t("without_location"),
            tree_set: [],
          };
        }
        areasMap[tree.area_id].tree_set.push(tree);
      }
    });
    setAreas(Object.values(areasMap));
  }, [allTrees, t]);

  if (isLoading) return <Spinner height={"70vh"} />;
  if (isError) return <ServerError />;

  return (
    <>
      <Breadcrumb title={t("without_project")} viewType={<ViewTypeButton id="without-project" />} />

      <Container className="project-container entity-list-page">
        <Tabs
          activeKey={activeTab}
          onSelect={handleTabSelect}
          id="without-project-tabs"
          className="project-detail-tabs mb-3"
        >
          <Tab eventKey="areas" title={t("areas")}>
            <Row className="entity-split-row g-3">
              <Col lg={id ? 6 : 12} className="entity-master-column">
                {areas && (
                  <IrrigationAreaList
                    areas={areas}
                    setIsUpdate={noop}
                    setDetail={noop}
                    setOpenForm={noop}
                  />
                )}
              </Col>
              {id && (
                <Col lg={6} className="entity-detail-column">
                  <div className="entity-detail-height-shell">
                    <IrrigationAreaDetail />
                  </div>
                </Col>
              )}
            </Row>
          </Tab>
          <Tab eventKey="trees" title={t("trees")}>
            {mobile ? (
              trees && <TreeListMobileView trees={trees} />
            ) : (
              <Row className="entity-split-row g-3">
                <Col ref={treeListColumnRef} lg={tid ? 6 : 12} className="entity-master-column">
                  {trees && (
                    <TreeList
                      trees={trees}
                      setAid={setAid}
                      setFormValue={setFormValue}
                      setOpenImageForm={setOpenImageForm}
                      openImageForm={openImageForm}
                      setIsUpdate={setIsTreeUpdate}
                      setOpenForm={setOpenTreeForm}
                      formValue={formValue}
                    />
                  )}
                </Col>
                {tid && (
                  <Col lg={6} className="tree-detail-column entity-detail-column">
                    <div
                      className="entity-detail-height-shell"
                      style={treeDetailHeight ? { height: `${treeDetailHeight}px` } : undefined}
                    >
                      <TreeDetail aid={aid} trees={trees} />
                    </div>
                  </Col>
                )}
              </Row>
            )}
          </Tab>
          <Tab eventKey="sensors" title={t("sensors")}>
            {mobile ? (
              sensors && <SensorMobileListView sensors={sensors} />
            ) : (
              <Row className="entity-split-row g-3">
                <Col ref={sensorListColumnRef} lg={sid ? 6 : 12} className="entity-master-column">
                  {sensors && <SensorList sensors={sensors} />}
                </Col>
                {sid && (
                  <Col lg={6} className="sensor-detail-column entity-detail-column">
                    <div
                      className="entity-detail-height-shell"
                      style={sensorDetailHeight ? { height: `${sensorDetailHeight}px` } : undefined}
                    >
                      <SensorDetail />
                    </div>
                  </Col>
                )}
              </Row>
            )}
          </Tab>
        </Tabs>
      </Container>
    </>
  );
}
